import React, { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import './Tooltip.scss';

export interface TooltipProps {
  delay?: number;
  direction?: TooltipDirection;
  content: ReactNode | string;
  children?: ReactNode;
  mode?: 'onClick' | 'onEnter';
  long?: boolean; // support long text and wrapping with breaklines
}

export type TooltipDirection = 'left' | 'right' | 'top' | 'bottom' | 'bottom-left';

const VIEWPORT_PADDING = 8;

const Tooltip: React.FC<TooltipProps> = props => {
  let timeout;
  const { delay = 200, direction = 'top', content, mode = 'onEnter', children = <></> } = props;

  const [active, setActive] = useState(false);
  const [adjustedStyle, setAdjustedStyle] = useState<React.CSSProperties>({});
  const tipRef = useRef<HTMLDivElement>(undefined);

  const adjustPosition = useCallback(() => {
    const tip = tipRef.current;
    if (!tip) {
      return;
    }

    const rect = tip.getBoundingClientRect();
    const style: React.CSSProperties = {};

    // Adjust horizontal overflow
    if (rect.left < VIEWPORT_PADDING) {
      style.left = `${VIEWPORT_PADDING - rect.left}px`;
      style.transform = 'translateX(0)';
    } else if (rect.right > window.innerWidth - VIEWPORT_PADDING) {
      style.left = 'auto';
      style.right = `${rect.right - window.innerWidth + VIEWPORT_PADDING}px`;
      style.transform = 'translateX(0)';
    }

    // Adjust vertical overflow
    if (rect.top < VIEWPORT_PADDING) {
      style.top = `${VIEWPORT_PADDING - rect.top}px`;
    } else if (rect.bottom > window.innerHeight - VIEWPORT_PADDING) {
      style.bottom = `${rect.bottom - window.innerHeight + VIEWPORT_PADDING}px`;
    }

    setAdjustedStyle(style);
  }, []);

  useLayoutEffect(() => {
    if (active) {
      adjustPosition();
    } else {
      setAdjustedStyle({});
    }
  }, [active, adjustPosition]);

  const showTip = () => {
    if (!content) {
      return;
    }
    timeout = setTimeout(() => {
      setActive(true);
    }, delay);
  };

  const hideTip = () => {
    clearInterval(timeout);
    setActive(false);
  };

  const attributes = {
    onMouseLeave: hideTip
  };

  switch (mode) {
    default:
    case 'onEnter':
      attributes['onMouseEnter'] = showTip;
      break;
    case 'onClick':
      attributes['onClick'] = showTip;
      break;
  }

  return (
    <div styleName="Tooltip-Wrapper"
         // When to show the tooltip
         {...attributes}
    >
      { children }
      { active && (
        <div
          ref={tipRef}
          style={adjustedStyle}
          styleName={classNames([ 'Tooltip-Tip', direction ], { 'Tooltip-Tip--long': props.long })}
        >
          { content }
        </div>
      ) }
    </div>
  );
};

export default Tooltip;
